import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { GripVertical, Plus, Edit, Trash2, Save, ArrowRight, Building2, Users, Wrench, Phone, Settings, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const hubConfig = {
  call_center: { label: 'Call Center', icon: Phone, color: 'bg-purple-100 text-purple-700 border-purple-200' },
  sales: { label: 'Sales', icon: Users, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  operations: { label: 'Operations', icon: Wrench, color: 'bg-orange-100 text-orange-700 border-orange-200' }
};

const stageColors = [
  'bg-slate-200', 'bg-gray-200', 'bg-red-200', 'bg-orange-200', 'bg-yellow-200',
  'bg-green-200', 'bg-emerald-200', 'bg-teal-200', 'bg-cyan-200', 'bg-blue-200',
  'bg-indigo-200', 'bg-violet-200', 'bg-purple-200', 'bg-pink-200', 'bg-rose-200'
];

export default function LeadFlowOrchestrator() {
  const [templates, setTemplates] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showStageDialog, setShowStageDialog] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [stageForm, setStageForm] = useState({
    name: '',
    hub_owner: 'call_center',
    color: 'bg-slate-200',
    visible_to_hubs: ['call_center', 'sales', 'operations'],
    auto_handoff_to: 'none',
    sla_hours: ''
  });
  const [hasChanges, setHasChanges] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/settings/workflows/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
      
      // Set default template as active
      const defaultTemplate = response.data.templates?.find(t => t.is_default) || response.data.templates?.[0];
      if (defaultTemplate) {
        setActiveTemplate(defaultTemplate);
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      toast.error('Failed to load workflow templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const saveTemplate = async () => {
    if (!activeTemplate) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(`${API}/settings/workflows/templates/${activeTemplate.id}`, {
        stages: activeTemplate.stages,
        handoff_rules: activeTemplate.handoff_rules
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Workflow saved successfully');
      setHasChanges(false);
    } catch (err) {
      console.error('Failed to save template:', err);
      toast.error('Failed to save workflow');
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination || !activeTemplate) return;
    
    const stages = Array.from(activeTemplate.stages);
    const [removed] = stages.splice(result.source.index, 1);
    stages.splice(result.destination.index, 0, removed);
    
    // Update order
    stages.forEach((stage, i) => stage.order = i);
    
    setActiveTemplate({ ...activeTemplate, stages });
    setHasChanges(true);
  };

  const openStageDialog = (stage = null) => {
    if (stage) {
      setEditingStage(stage);
      setStageForm({
        name: stage.name,
        hub_owner: stage.hub_owner,
        color: stage.color,
        visible_to_hubs: stage.visible_to_hubs || ['call_center', 'sales', 'operations'],
        auto_handoff_to: stage.auto_handoff_to || 'none',
        sla_hours: stage.sla_hours?.toString() || ''
      });
    } else {
      setEditingStage(null);
      setStageForm({
        name: '',
        hub_owner: 'call_center',
        color: 'bg-slate-200',
        visible_to_hubs: ['call_center', 'sales', 'operations'],
        auto_handoff_to: 'none',
        sla_hours: ''
      });
    }
    setShowStageDialog(true);
  };

  const saveStage = () => {
    if (!activeTemplate || !stageForm.name) return;
    
    const stages = [...activeTemplate.stages];
    const stageData = {
      id: editingStage?.id || `stage-${Date.now()}`,
      name: stageForm.name,
      hub_owner: stageForm.hub_owner,
      color: stageForm.color,
      visible_to_hubs: stageForm.visible_to_hubs,
      auto_handoff_to: stageForm.auto_handoff_to === 'none' ? null : stageForm.auto_handoff_to,
      sla_hours: stageForm.sla_hours ? parseInt(stageForm.sla_hours) : null,
      order: editingStage ? editingStage.order : stages.length,
      is_active: true
    };
    
    if (editingStage) {
      const idx = stages.findIndex(s => s.id === editingStage.id);
      if (idx >= 0) stages[idx] = stageData;
    } else {
      stages.push(stageData);
    }
    
    setActiveTemplate({ ...activeTemplate, stages });
    setShowStageDialog(false);
    setHasChanges(true);
  };

  const deleteStage = (stageId) => {
    if (!activeTemplate) return;
    const stages = activeTemplate.stages.filter(s => s.id !== stageId);
    stages.forEach((s, i) => s.order = i);
    setActiveTemplate({ ...activeTemplate, stages });
    setHasChanges(true);
  };

  const toggleVisibility = (hub) => {
    const visible = [...stageForm.visible_to_hubs];
    const idx = visible.indexOf(hub);
    if (idx >= 0) {
      visible.splice(idx, 1);
    } else {
      visible.push(hub);
    }
    setStageForm({ ...stageForm, visible_to_hubs: visible });
  };

  // Group stages by hub owner for visualization
  const groupedStages = activeTemplate?.stages?.reduce((acc, stage) => {
    const hub = stage.hub_owner || 'call_center';
    if (!acc[hub]) acc[hub] = [];
    acc[hub].push(stage);
    return acc;
  }, {}) || {};

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Lead Flow Orchestrator</h1>
          <p className="text-muted-foreground">Configure board stages and hub ownership rules</p>
        </div>
        <div className="flex gap-2">
          {hasChanges && (
            <Badge variant="secondary" className="mr-2">Unsaved changes</Badge>
          )}
          <Button onClick={saveTemplate} disabled={!hasChanges}>
            <Save className="h-4 w-4 mr-2" />
            Save Workflow
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : !activeTemplate ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Settings className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No workflow template found</p>
            <Button className="mt-4">Create Default Template</Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Visual Hub Overview */}
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(hubConfig).map(([hubKey, config]) => {
              const hubStages = groupedStages[hubKey] || [];
              const HubIcon = config.icon;
              return (
                <Card key={hubKey} className={`border-2 ${config.color.split(' ')[0].replace('100', '300')}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <HubIcon className="h-5 w-5" />
                      <CardTitle className="text-lg">{config.label} Hub</CardTitle>
                    </div>
                    <CardDescription>{hubStages.length} stages owned</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1">
                      {hubStages.sort((a, b) => a.order - b.order).map((stage) => (
                        <div
                          key={stage.id}
                          className={`px-3 py-2 rounded text-sm ${stage.color}`}
                        >
                          {stage.name}
                          {stage.auto_handoff_to && (
                            <ArrowRight className="h-3 w-3 inline ml-1" />
                          )}
                        </div>
                      ))}
                      {hubStages.length === 0 && (
                        <p className="text-sm text-muted-foreground">No stages assigned</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Stage Management */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Workflow Stages</CardTitle>
                  <CardDescription>Drag to reorder, click to edit</CardDescription>
                </div>
                <Button onClick={() => openStageDialog()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Stage
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="stages">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                      {activeTemplate.stages.sort((a, b) => a.order - b.order).map((stage, index) => {
                        const hubCfg = hubConfig[stage.hub_owner] || hubConfig.call_center;
                        const HubIcon = hubCfg.icon;
                        return (
                          <Draggable key={stage.id} draggableId={stage.id} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`flex items-center gap-4 p-4 border rounded-lg ${
                                  snapshot.isDragging ? 'shadow-lg bg-accent' : 'bg-card'
                                }`}
                              >
                                <div {...provided.dragHandleProps}>
                                  <GripVertical className="h-5 w-5 text-muted-foreground" />
                                </div>
                                <div className={`w-4 h-4 rounded ${stage.color}`} />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{stage.name}</span>
                                    <Badge variant="outline" className={`text-xs ${hubCfg.color}`}>
                                      <HubIcon className="h-3 w-3 mr-1" />
                                      {hubCfg.label}
                                    </Badge>
                                  </div>
                                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                    <span>Visible to: {stage.visible_to_hubs?.join(', ') || 'All'}</span>
                                    {stage.auto_handoff_to && (
                                      <span className="flex items-center">
                                        <ArrowRight className="h-3 w-3 mx-1" />
                                        Handoff to {hubConfig[stage.auto_handoff_to]?.label}
                                      </span>
                                    )}
                                    {stage.sla_hours && (
                                      <span>• SLA: {stage.sla_hours}h</span>
                                    )}
                                  </div>
                                </div>
                                <Button variant="ghost" size="sm" onClick={() => openStageDialog(stage)}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => deleteStage(stage.id)}>
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </CardContent>
          </Card>
        </>
      )}

      {/* Stage Dialog */}
      <Dialog open={showStageDialog} onOpenChange={setShowStageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingStage ? 'Edit Stage' : 'Add Stage'}</DialogTitle>
            <DialogDescription>Configure stage properties and ownership</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Stage Name</Label>
              <Input
                value={stageForm.name}
                onChange={(e) => setStageForm({ ...stageForm, name: e.target.value })}
                placeholder="e.g., Intro Call"
              />
            </div>
            <div>
              <Label>Hub Owner</Label>
              <Select
                value={stageForm.hub_owner}
                onValueChange={(v) => setStageForm({ ...stageForm, hub_owner: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(hubConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <config.icon className="h-4 w-4" />
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {stageColors.map((color) => (
                  <button
                    key={color}
                    onClick={() => setStageForm({ ...stageForm, color })}
                    className={`w-8 h-8 rounded ${color} ${
                      stageForm.color === color ? 'ring-2 ring-primary ring-offset-2' : ''
                    }`}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label>Visible To</Label>
              <div className="flex gap-2 mt-2">
                {Object.entries(hubConfig).map(([key, config]) => (
                  <Button
                    key={key}
                    variant={stageForm.visible_to_hubs.includes(key) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleVisibility(key)}
                  >
                    <config.icon className="h-4 w-4 mr-1" />
                    {config.label}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <Label>Auto Handoff To (Optional)</Label>
              <Select
                value={stageForm.auto_handoff_to}
                onValueChange={(v) => setStageForm({ ...stageForm, auto_handoff_to: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="No automatic handoff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No automatic handoff</SelectItem>
                  {Object.entries(hubConfig)
                    .filter(([key]) => key !== stageForm.hub_owner)
                    .map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <config.icon className="h-4 w-4" />
                          {config.label}
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>SLA (hours, optional)</Label>
              <Input
                type="number"
                value={stageForm.sla_hours}
                onChange={(e) => setStageForm({ ...stageForm, sla_hours: e.target.value })}
                placeholder="e.g., 24"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStageDialog(false)}>Cancel</Button>
            <Button onClick={saveStage} disabled={!stageForm.name}>Save Stage</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
